/**
 * Cross-Runtime Base64URL-Encoder/Decoder (RFC 4648 §5, ohne Padding).
 *
 * Implementiert ohne `atob`/`btoa` (laut Konventionen verboten) und ohne
 * Node-`Buffer`, damit das Modul sowohl im Browser (Frontend) als auch in Node
 * (Backend) identisch funktioniert.
 *
 * Alphabet: `A-Z` (0–25), `a-z` (26–51), `0-9` (52–61), `-` (62), `_` (63).
 */

const BASE64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const BITS_PER_BYTE = 8;
const BITS_PER_BASE64_CHAR = 6;
const BYTES_PER_TRIPLE = 3;
const CHARS_PER_TRIPLE = 4;
const SIX_BIT_MASK = 0b111111;
const EIGHT_BIT_MASK = 0xff;
const ASCII_TABLE_SIZE = 128;
const INVALID_CHAR = -1;

/**
 * Vorberechnete Charcode-Tabelle: 6-Bit-Wert → ASCII-Code des Base64URL-Zeichens.
 * Lookup über typed array vermeidet `string | undefined` aus `noUncheckedIndexedAccess`.
 */
const BASE64URL_CHARCODES: Uint8Array = (() => {
  const codes = new Uint8Array(BASE64URL_ALPHABET.length);
  for (let index = 0; index < BASE64URL_ALPHABET.length; index += 1) {
    codes[index] = BASE64URL_ALPHABET.charCodeAt(index);
  }
  return codes;
})();

/**
 * Lookup-Tabelle für ASCII-Code → 6-Bit-Wert.
 * `INVALID_CHAR` markiert Zeichen außerhalb des Alphabets.
 */
const BASE64URL_LOOKUP: Int8Array = (() => {
  const table = new Int8Array(ASCII_TABLE_SIZE).fill(INVALID_CHAR);
  for (let index = 0; index < BASE64URL_ALPHABET.length; index += 1) {
    table[BASE64URL_ALPHABET.charCodeAt(index)] = index;
  }
  return table;
})();

/**
 * Kodiert ein Byte-Array als Base64URL-String (ohne Padding).
 *
 * Verarbeitet 3-Byte-Tripel in 4 Zeichen; an einem 1- oder 2-Byte-Rest werden
 * 2 bzw. 3 Zeichen ausgegeben (kein `=`-Padding wie in Base64-Standard).
 *
 * @param bytes Eingabebytes (beliebige Länge inkl. 0).
 * @returns Base64URL-kodierter String.
 */
export function encodeBase64Url(bytes: Uint8Array): string {
  const fullTripleEnd = bytes.length - (bytes.length % BYTES_PER_TRIPLE);
  const outputLength = computeCharCount(bytes.length);
  const outputCharcodes = new Uint8Array(outputLength);
  let writeIndex = 0;

  for (let readIndex = 0; readIndex < fullTripleEnd; readIndex += BYTES_PER_TRIPLE) {
    const triple =
      ((bytes[readIndex] ?? 0) << 16) |
      ((bytes[readIndex + 1] ?? 0) << 8) |
      (bytes[readIndex + 2] ?? 0);
    writeIndex = writeTripleChars(outputCharcodes, writeIndex, triple);
  }

  appendRemainderChars(bytes, fullTripleEnd, outputCharcodes, writeIndex);
  return String.fromCharCode(...outputCharcodes);
}

/**
 * Dekodiert einen Base64URL-String zu einem `Uint8Array`.
 *
 * Akzeptiert sowohl Eingaben mit als auch ohne `=`-Padding (Padding wird vor
 * der Dekodierung verworfen). Bei ungültigen Zeichen oder ungültiger Länge
 * wird `Error('INVALID_BASE64URL')` geworfen.
 *
 * @param input Base64URL-kodierter String.
 * @returns Dekodierte Bytes.
 */
export function decodeBase64Url(input: string): Uint8Array {
  const trimmed = stripPadding(input);
  if (trimmed.length % CHARS_PER_TRIPLE === 1) {
    throw new Error('INVALID_BASE64URL');
  }

  const bytes = new Uint8Array(computeByteLength(trimmed.length));
  let writeIndex = 0;
  let buffer = 0;
  let bitsInBuffer = 0;

  for (let readIndex = 0; readIndex < trimmed.length; readIndex += 1) {
    const sixBits = lookupCharValue(trimmed.charCodeAt(readIndex));
    buffer = (buffer << BITS_PER_BASE64_CHAR) | sixBits;
    bitsInBuffer += BITS_PER_BASE64_CHAR;
    if (bitsInBuffer >= BITS_PER_BYTE) {
      bitsInBuffer -= BITS_PER_BYTE;
      bytes[writeIndex] = (buffer >> bitsInBuffer) & EIGHT_BIT_MASK;
      writeIndex += 1;
    }
  }

  return bytes;
}

/**
 * Schreibt vier Base64URL-Zeichen für ein 24-Bit-Tripel in den Output-Buffer.
 */
function writeTripleChars(output: Uint8Array, offset: number, triple: number): number {
  output[offset] = charcodeAt((triple >> 18) & SIX_BIT_MASK);
  output[offset + 1] = charcodeAt((triple >> 12) & SIX_BIT_MASK);
  output[offset + 2] = charcodeAt((triple >> 6) & SIX_BIT_MASK);
  output[offset + 3] = charcodeAt(triple & SIX_BIT_MASK);
  return offset + CHARS_PER_TRIPLE;
}

/**
 * Hängt die Base64URL-Zeichen für 1- oder 2-Byte-Reste an.
 *
 * - 1 Byte → 2 Zeichen (12 Bit Eingabe, 6 Bit padding ignoriert).
 * - 2 Bytes → 3 Zeichen (16 Bit Eingabe, 2 Bit padding ignoriert).
 */
function appendRemainderChars(
  bytes: Uint8Array,
  start: number,
  output: Uint8Array,
  offset: number,
): void {
  const remainder = bytes.length - start;
  if (remainder === 0) {
    return;
  }
  if (remainder === 1) {
    const single = (bytes[start] ?? 0) << 16;
    output[offset] = charcodeAt((single >> 18) & SIX_BIT_MASK);
    output[offset + 1] = charcodeAt((single >> 12) & SIX_BIT_MASK);
    return;
  }
  const pair = ((bytes[start] ?? 0) << 16) | ((bytes[start + 1] ?? 0) << 8);
  output[offset] = charcodeAt((pair >> 18) & SIX_BIT_MASK);
  output[offset + 1] = charcodeAt((pair >> 12) & SIX_BIT_MASK);
  output[offset + 2] = charcodeAt((pair >> 6) & SIX_BIT_MASK);
}

/**
 * Liefert den ASCII-Charcode für einen 6-Bit-Wert.
 */
function charcodeAt(sixBitValue: number): number {
  return BASE64URL_CHARCODES[sixBitValue] ?? 0;
}

/**
 * Entfernt optionales `=`-Padding (RFC 4648 §5 nutzt es nicht).
 */
function stripPadding(input: string): string {
  let end = input.length;
  const paddingCharCode = '='.charCodeAt(0);
  while (end > 0 && input.charCodeAt(end - 1) === paddingCharCode) {
    end -= 1;
  }
  return end === input.length ? input : input.slice(0, end);
}

/**
 * Berechnet die Byte-Länge für eine gegebene Anzahl an Base64URL-Zeichen.
 *
 * - 4 Zeichen → 3 Bytes (volles Tripel).
 * - 2 Zeichen → 1 Byte (1-Byte-Rest).
 * - 3 Zeichen → 2 Bytes (2-Byte-Rest).
 */
function computeByteLength(charCount: number): number {
  const fullTriples = Math.floor(charCount / CHARS_PER_TRIPLE);
  const remainder = charCount % CHARS_PER_TRIPLE;
  const remainderBytes = remainder === 0 ? 0 : remainder - 1;
  return fullTriples * BYTES_PER_TRIPLE + remainderBytes;
}

/**
 * Berechnet die Anzahl an Base64URL-Zeichen für eine gegebene Byte-Länge.
 */
function computeCharCount(byteCount: number): number {
  const fullTriples = Math.floor(byteCount / BYTES_PER_TRIPLE);
  const remainder = byteCount % BYTES_PER_TRIPLE;
  const remainderChars = remainder === 0 ? 0 : remainder + 1;
  return fullTriples * CHARS_PER_TRIPLE + remainderChars;
}

/**
 * Schlägt einen Zeichencode im Base64URL-Alphabet nach.
 * Wirft `Error('INVALID_BASE64URL')` bei ungültigen oder
 * Nicht-ASCII-Zeichen.
 */
function lookupCharValue(charCode: number): number {
  if (charCode < 0 || charCode >= BASE64URL_LOOKUP.length) {
    throw new Error('INVALID_BASE64URL');
  }
  const value = BASE64URL_LOOKUP[charCode];
  if (value === undefined || value === INVALID_CHAR) {
    throw new Error('INVALID_BASE64URL');
  }
  return value;
}
